import { Link, useLocation } from "wouter";
import { Wordmark } from "@workspace/brand-react";
import { Menu, X, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { cn, APP_SIGN_IN_URL, APP_SIGN_UP_URL } from "@/lib/utils";
import { INDUSTRIES } from "@/lib/industries";

const NAV = [
  { href: "/how-it-works", label: "How it works" },
  { href: "/pricing", label: "Pricing" },
  { href: "/security", label: "Security" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

export function Header() {
  const [open, setOpen] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [location] = useLocation();
  const industriesRef = useRef<HTMLDivElement>(null);
  const isOnIndustries = location.startsWith("/industries");

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (industriesRef.current && !industriesRef.current.contains(e.target as Node)) {
        setIndustriesOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header className="sticky top-0 z-50 bg-[hsl(var(--brand-bone))]/85 backdrop-blur border-b border-[hsl(var(--brand-sand))]">
      <div className="mx-auto max-w-6xl px-6 h-16 flex items-center justify-between">
        <Link href="/" className="flex items-center" data-testid="link-home">
          <Wordmark size="md" />
        </Link>
        <nav className="hidden md:flex items-center gap-7">
          <div className="relative" ref={industriesRef}>
            <button
              type="button"
              onClick={() => setIndustriesOpen((v) => !v)}
              aria-expanded={industriesOpen}
              aria-haspopup="true"
              className={cn(
                "text-sm transition-colors inline-flex items-center gap-1",
                isOnIndustries
                  ? "text-[hsl(var(--brand-forest))] font-medium"
                  : "text-[hsl(var(--brand-slate))] hover:text-[hsl(var(--brand-charcoal))]",
              )}
              data-testid="button-nav-industries"
            >
              Industries
              <ChevronDown className={cn("w-3.5 h-3.5 transition-transform", industriesOpen && "rotate-180")} />
            </button>
            {industriesOpen && (
              <div
                className="absolute left-0 mt-2 w-72 rounded-xl border border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))] shadow-lg overflow-hidden"
                data-testid="menu-industries"
              >
                <div className="py-2">
                  {INDUSTRIES.map((i) => (
                    <Link
                      key={i.slug}
                      href={`/industries/${i.slug}`}
                      onClick={() => setIndustriesOpen(false)}
                      className="block px-4 py-2.5 text-sm text-[hsl(var(--brand-charcoal))] hover:bg-white"
                      data-testid={`menu-industry-${i.slug}`}
                    >
                      <div className="font-medium">{i.name}</div>
                    </Link>
                  ))}
                  <div className="border-t border-[hsl(var(--brand-sand))] mt-2 pt-2">
                    <Link
                      href="/industries"
                      onClick={() => setIndustriesOpen(false)}
                      className="block px-4 py-2.5 text-sm text-[hsl(var(--brand-forest))] hover:bg-white font-medium"
                    >
                      All industries →
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
          {NAV.map((n) => (
            <Link
              key={n.href}
              href={n.href}
              className={cn(
                "text-sm transition-colors",
                location === n.href
                  ? "text-[hsl(var(--brand-forest))] font-medium"
                  : "text-[hsl(var(--brand-slate))] hover:text-[hsl(var(--brand-charcoal))]",
              )}
              data-testid={`link-nav-${n.href.slice(1)}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <a href={APP_SIGN_IN_URL} className="text-sm text-[hsl(var(--brand-slate))] hover:text-[hsl(var(--brand-charcoal))]" data-testid="link-signin">
            Sign in
          </a>
          <Link
            href="/contact"
            className="text-sm text-[hsl(var(--brand-slate))] hover:text-[hsl(var(--brand-charcoal))] border border-[hsl(var(--brand-sand))] px-4 py-2 rounded transition-colors"
            data-testid="link-book-demo"
          >
            Book a demo
          </Link>
          <a
            href={APP_SIGN_UP_URL}
            className="text-sm font-medium bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-4 py-2 rounded hover:bg-[hsl(168,25%,22%)] transition-colors"
            data-testid="button-signup"
          >
            Get started
          </a>
        </div>
        <button
          className="md:hidden p-2 text-[hsl(var(--brand-charcoal))]"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="button-menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[hsl(var(--brand-sand))] bg-[hsl(var(--brand-bone))]">
          <div className="px-6 py-4 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[hsl(var(--brand-slate))]">Industries</div>
              {INDUSTRIES.map((i) => (
                <Link
                  key={i.slug}
                  href={`/industries/${i.slug}`}
                  className="text-sm text-[hsl(var(--brand-charcoal))] pl-2"
                  onClick={() => setOpen(false)}
                >
                  {i.name}
                </Link>
              ))}
              <Link href="/industries" className="text-sm text-[hsl(var(--brand-forest))] font-medium pl-2" onClick={() => setOpen(false)}>
                All industries →
              </Link>
            </div>
            <div className="border-t border-[hsl(var(--brand-sand))] pt-3 flex flex-col gap-3">
              {NAV.map((n) => (
                <Link key={n.href} href={n.href} className="text-sm text-[hsl(var(--brand-charcoal))]" onClick={() => setOpen(false)}>
                  {n.label}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-2 pt-3 border-t border-[hsl(var(--brand-sand))]">
              <a href={APP_SIGN_IN_URL} className="text-sm text-[hsl(var(--brand-slate))]">Sign in</a>
              <Link href="/contact" className="text-sm text-[hsl(var(--brand-charcoal))] border border-[hsl(var(--brand-sand))] px-4 py-2 rounded text-center" onClick={() => setOpen(false)}>
                Book a demo
              </Link>
              <a href={APP_SIGN_UP_URL} className="text-sm font-medium bg-[hsl(var(--brand-forest))] text-[hsl(var(--brand-bone))] px-4 py-2 rounded text-center">
                Get started
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
